import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { ToastProvider } from '@/hooks/useToast.tsx';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Spinner } from '@/components/ui/spinner';
import { SuperuserRoute } from '@/components/routing/SuperuserRoute';
import { API_CONSTANTS } from '@/constants/api';
import './App.css';

// EPIC-32: Code splitting - lazy load page components for smaller initial bundle
const Login = lazy(() => import('@/pages/Login').then(m => ({ default: m.Login })));
const MediaGallery = lazy(() => import('@/pages/MediaGallery').then(m => ({ default: m.MediaGallery })));
const VideoDetail = lazy(() => import('@/pages/VideoDetail').then(m => ({ default: m.VideoDetail })));
const PromptPacks = lazy(() => import('@/pages/PromptPacks').then(m => ({ default: m.PromptPacks })));
const PromptWizard = lazy(() => import('@/pages/PromptWizard/PromptWizard'));
const Settings = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })));
const I18nTest = lazy(() => import('@/pages/I18nTest').then(m => ({ default: m.I18nTest })));
const JudgeSetup = lazy(() => import('@/pages/JudgeSetup').then(m => ({ default: m.JudgeSetup })));
const PromptEval = lazy(() => import('@/pages/PromptEval').then(m => ({ default: m.PromptEval })));
const WatchlistPage = lazy(() => import('@/pages/Watchlist').then(m => ({ default: m.WatchlistPage })));
const AlertsPage = lazy(() => import('@/pages/Alerts').then(m => ({ default: m.AlertsPage })));
const SearchByImagePage = lazy(() => import('@/pages/SearchByImage').then(m => ({ default: m.SearchByImagePage })));

// EPIC-32: Page loading fallback component
function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

// TanStack Query client for data fetching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: API_CONSTANTS.DEFAULT_STALE_TIME,
      retry: 1,
    },
  },
});

/**
 * Main App Component with Multi-Language Support (EPIC-16)
 *
 * Language preference is loaded from database after authentication via LanguageLoader.
 * LanguageLoader is placed inside AuthProvider to react to auth state changes.
 *
 * EPIC-32: Code splitting implemented - pages loaded on-demand for faster initial load
 */
function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <Router>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Redirect root to media gallery */}
                  <Route path="/" element={<Navigate to="/media-gallery" replace />} />

                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />

                  {/* Public routes - POC mode (no authentication required) */}
                  <Route path="/media-gallery" element={<MediaGallery />} />
                  <Route path="/videos/:id" element={<VideoDetail />} />
                  <Route path="/prompt-packs" element={<PromptPacks />} />
                  <Route path="/prompt-wizard" element={<PromptWizard />} />
                  <Route path="/settings" element={<SuperuserRoute><Settings /></SuperuserRoute>} />

                  {/* EPIC-20: Prompt Evaluation System */}
                  <Route path="/judge-setup" element={<JudgeSetup />} />
                  <Route path="/prompt-eval" element={<PromptEval />} />

                  {/* Face ReID: Watchlist */}
                  <Route path="/watchlist" element={<WatchlistPage />} />

                  {/* Face ReID: Unified Alerts */}
                  <Route path="/alerts" element={<AlertsPage />} />

                  {/* Face ReID: Search by Face */}
                  <Route path="/search-by-image" element={<SearchByImagePage />} />

                  {/* EPIC-16: i18n test page */}
                  <Route path="/i18n-test" element={<I18nTest />} />

                  {/* Catch-all redirect */}
                  <Route path="*" element={<Navigate to="/media-gallery" replace />} />
                </Routes>
              </Suspense>
            </Router>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
